import * as ROUTES from '../common/routes';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import React, { useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import UploadComment from './UploadComment';
import clsx from 'clsx';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import { makeStyles, createStyles, StyleRules } from '@mui/styles';
import { useTranslations } from '../common/hooks/useTranslations';
import ArticleIcon from '@mui/icons-material/Article';

const useStyles = makeStyles(
  (theme): StyleRules =>
    createStyles({
      imagesContainerStyle: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        flex: 1,
      },
      filePreviewStyle: {
        borderRadius: '8px',
        overflow: 'hidden',
        width: '60px',
        height: '60px',
        margin: '0 10px 10px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: COLORS.circleGray,
        border: `1px solid ${COLORS.appButtonsLightGray}`,
      },
      imageStyle: {
        objectFit: 'cover',
      },
      imgWrapperStyle: {
        position: 'relative',
      },
      addCommentStyle: {
        position: 'absolute',
        color: 'white',
        padding: '2px',
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            opacity: 0.7,
            transition: '0.5s ease',
          },
        },
        filter: 'drop-shadow(0 6px 6px rgb(0 0 0 / 1))',
      },
      [`@media (max-width: 560px)`]: {
        imagesContainerStyle: {
          marginBottom: '20px',
        },
      },
    })
);

const UploadPreview = ({ files, onAddComment, fileComments }): JSX.Element => {
  const classes = useStyles();
  const [fileUUid, setCommendDialog] = useState('');
  const hasFiles = Boolean(files?.length);

  const addPlaceholder = useTranslations('add_image_placeholder');

  const onPlusClick = (event: any) => {
    event.stopPropagation();
    event.preventDefault();

    const uuid = event?.currentTarget?.getAttribute('data-uuid');
    setCommendDialog(uuid);
  };

  const onCloseDialog = (event?: React.SyntheticEvent) => {
    event?.stopPropagation();
    event?.preventDefault();

    setCommendDialog('');
  };

  const getFilesPreview = (file, index) => {
    if (file?.type.includes('image/')) {
      const imgUrl = URL.createObjectURL(file);
      const uuid = file.uuid;
      const comment = fileComments?.[uuid] || '';

      return (
        <div className={classes.imgWrapperStyle} key={index}>
          <Tooltip title={addPlaceholder} placement={'top-start'} arrow>
            <AddCircleOutlineIcon
              className={classes.addCommentStyle}
              onClick={onPlusClick}
              data-uuid={uuid}
            />
          </Tooltip>
          <img
            src={imgUrl}
            className={clsx(classes.filePreviewStyle, classes.imageStyle)}
          />
        </div>
      );
    }

    if (
      // file?.type.includes('document') ||
      // file?.type === 'application/msword'
      file?.type === 'application/msword' ||
      file?.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      return (
        <div className={classes.filePreviewStyle} key={index}>
          <ArticleIcon sx={{ width: '36px', height: '36px' }} />
        </div>
      );
    }

    if (file?.type === 'application/pdf') {
      return (
        <div className={classes.filePreviewStyle} key={index}>
          <PictureAsPdfIcon sx={{ width: '36px', height: '36px' }} />
        </div>
      );
    }

    if (file?.type.includes('audio/')) {
      return (
        <div className={classes.filePreviewStyle} key={index}>
          <AudioFileIcon sx={{ width: '36px', height: '36px' }} />
        </div>
      );
    }

    return <div key={index} className={classes.filePreviewStyle} />;
  };

  return (
    <>
      <div className={classes.imagesContainerStyle}>
        {hasFiles && files.map(getFilesPreview)}
      </div>
      <UploadComment
        onClose={onCloseDialog}
        fileUUid={fileUUid}
        onAddComment={onAddComment}
        comment={fileComments?.[fileUUid]}
      />
    </>
  );
};

export default UploadPreview;
